import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { Switch } from '../components/ui/switch';
import { Checkbox } from '../components/ui/checkbox';
import { 
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Progress } from '../components/ui/progress';
import { submissionsAPI } from '../lib/api';
import { toast } from 'sonner';
import { 
    ArrowLeft, 
    ArrowRight, 
    Send, 
    CheckCircle2,
    Building2,
    MapPin,
    User,
    Info,
    Briefcase,
    GraduationCap,
    HeartHandshake,
    Landmark,
    Bus,
    School,
    UserPlus,
    ExternalLink
} from 'lucide-react';

const US_STATES = [
    "Alabama", "Alaska", "Arizona", "Arkansas", "California", "Colorado",
    "Connecticut", "Delaware", "Florida", "Georgia", "Hawaii", "Idaho",
    "Illinois", "Indiana", "Iowa", "Kansas", "Kentucky", "Louisiana",
    "Maine", "Maryland", "Massachusetts", "Michigan", "Minnesota",
    "Mississippi", "Missouri", "Montana", "Nebraska", "Nevada",
    "New Hampshire", "New Jersey", "New Mexico", "New York",
    "North Carolina", "North Dakota", "Ohio", "Oklahoma", "Oregon",
    "Pennsylvania", "Rhode Island", "South Carolina", "South Dakota",
    "Tennessee", "Texas", "Utah", "Vermont", "Virginia", "Washington",
    "West Virginia", "Wisconsin", "Wyoming", "National"
];

const STAKEHOLDER_TYPES = [
    { value: "Employer", label: "Employer", icon: Briefcase, description: "Businesses offering employment opportunities" },
    { value: "Service/Support Provider", label: "Service/Support Provider", icon: HeartHandshake, description: "Organizations providing services for employment" },
    { value: "College or Training Provider", label: "College or Training Provider", icon: GraduationCap, description: "Educational institutions and training programs" },
    { value: "High School", label: "High School", icon: School, description: "Schools with ESE transition programs" },
    { value: "Transportation", label: "Transportation", icon: Bus, description: "Specialized transportation services" },
    { value: "Government", label: "Government", icon: Landmark, description: "Government agencies supporting autistic adults" },
];

const REQUESTER_ROLES = [
    "Chapter Director",
    "HAAPE Staff",
    "Partner Organization",
    "Community Member",
    "Other"
];

const ApplyMembershipPage = () => {
    const navigate = useNavigate();
    const [step, setStep] = useState(1);
    const [submitting, setSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    
    const [formData, setFormData] = useState({
        // Step 1: Organization Info
        name: '',
        mission: '',
        website: '',
        state: '',
        city: '',
        
        // Organization Contact
        contact_name: '',
        contact_email: '',
        contact_phone: '',
        
        // Step 2: Stakeholder Type
        stakeholder_types: [],
        
        // Employer fields
        employer_type: '',
        job_types: '',
        
        // Service/Support Provider fields
        services_for_employment: '',
        
        // College/Training Provider fields
        has_neurodiverse_programs: false,
        program_description: '',
        
        // High School fields
        has_ese_transition: false,
        ese_program_description: '',
        
        // Transportation fields
        has_specialized_transport: false,
        transport_description: '',
        
        // Government fields
        government_support_description: '',
        
        // Additional Info
        other_info: '',
        facebook_url: '',
        linkedin_url: '',
        nd_population_served: '',
        
        // Step 3: Requester Info
        requester_name: '',
        requester_email: '',
        requester_role: '',
        chapter_location: '',
        
        // Terms Agreement
        agreed_to_terms: false,
    });

    const [errors, setErrors] = useState({});

    const handleChange = (field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));
        if (errors[field]) {
            setErrors(prev => ({ ...prev, [field]: null }));
        }
    };

    const validateStep1 = () => {
        const newErrors = {};
        if (!formData.name.trim()) newErrors.name = 'Organization name is required';
        if (!formData.mission.trim()) newErrors.mission = 'Mission statement is required';
        if (!formData.state) newErrors.state = 'State is required';
        if (!formData.contact_name.trim()) newErrors.contact_name = 'Contact name is required';
        if (!formData.contact_email.trim()) newErrors.contact_email = 'Contact email is required';
        if (formData.contact_email && !/\S+@\S+\.\S+/.test(formData.contact_email)) {
            newErrors.contact_email = 'Invalid email format';
        }
        
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const validateStep2 = () => {
        const newErrors = {};
        if (!formData.stakeholder_types.length) newErrors.stakeholder_types = 'Please select at least one organization type';
        
        // Type-specific validation
        if (formData.stakeholder_types.includes('Employer')) {
            if (!formData.employer_type.trim()) newErrors.employer_type = 'Please describe the type of employer';
        }
        if (formData.stakeholder_types.includes('Service/Support Provider')) {
            if (!formData.services_for_employment.trim()) newErrors.services_for_employment = 'Please describe services provided';
        }
        if (formData.stakeholder_types.includes('Government')) {
            if (!formData.government_support_description.trim()) newErrors.government_support_description = 'Please describe how they support autistic adults';
        }
        
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const validateStep3 = () => {
        const newErrors = {};
        if (!formData.requester_name.trim()) newErrors.requester_name = 'Your name is required';
        if (!formData.requester_email.trim()) newErrors.requester_email = 'Your email is required';
        if (!formData.requester_role) newErrors.requester_role = 'Your role is required';
        
        if (formData.requester_email && !/\S+@\S+\.\S+/.test(formData.requester_email)) {
            newErrors.requester_email = 'Invalid email format';
        }
        
        if (!formData.agreed_to_terms) {
            newErrors.agreed_to_terms = 'You must agree to the Terms & Conditions to submit';
        }
        
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleNext = () => {
        if (step === 1 && validateStep1()) setStep(2);
        else if (step === 2 && validateStep2()) setStep(3);
    };

    const handleBack = () => {
        if (step > 1) setStep(step - 1);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!validateStep3()) return;
        
        setSubmitting(true);
        
        try {
            await submissionsAPI.create(formData);
            setSubmitted(true);
            toast.success('Your membership application has been submitted for review!');
        } catch (error) {
            console.error('Submission error:', error);
            toast.error('Failed to submit. Please try again.');
        } finally {
            setSubmitting(false);
        }
    };

    const progressPercent = (step / 3) * 100;

    if (submitted) {
        return (
            <div className="min-h-screen bg-background flex items-center justify-center p-4" data-testid="submission-success">
                <Card className="max-w-md w-full">
                    <CardContent className="pt-8 text-center">
                        <div className="w-16 h-16 bg-green-100 dark:bg-green-900 rounded-full flex items-center justify-center mx-auto mb-6">
                            <CheckCircle2 className="h-8 w-8 text-green-600 dark:text-green-400" />
                        </div>
                        <h2 className="font-heading text-2xl font-bold mb-3">
                            Application Submitted!
                        </h2>
                        <p className="text-muted-foreground mb-6">
                            Your membership application has been submitted and will be reviewed by our team. 
                            We'll reach out if we need any additional information.
                        </p>
                        <div className="flex flex-col sm:flex-row gap-3 justify-center">
                            <Button onClick={() => navigate('/')} data-testid="go-home-btn">
                                Go Home
                            </Button>
                            <Button 
                                variant="outline" 
                                onClick={() => {
                                    setSubmitted(false);
                                    setStep(1);
                                    setFormData({
                                        name: '', mission: '', website: '', state: '', city: '',
                                        contact_name: '', contact_email: '', contact_phone: '',
                                        stakeholder_types: [], employer_type: '', job_types: '',
                                        services_for_employment: '', has_neurodiverse_programs: false,
                                        program_description: '', has_ese_transition: false,
                                        ese_program_description: '', has_specialized_transport: false,
                                        transport_description: '', government_support_description: '',
                                        other_info: '', facebook_url: '', linkedin_url: '',
                                        nd_population_served: '', requester_name: '', requester_email: '',
                                        requester_role: '', chapter_location: '',
                                    });
                                }}
                                data-testid="submit-another-btn"
                            >
                                Submit Another Application
                            </Button>
                        </div>
                    </CardContent>
                </Card>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-background" data-testid="apply-membership-page">
            <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
                {/* Header */}
                <div className="text-center mb-8">
                    <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                        <UserPlus className="h-8 w-8 text-primary" />
                    </div>
                    <h1 className="font-heading text-3xl font-bold mb-3">
                        Apply For Membership
                    </h1>
                    <p className="text-muted-foreground max-w-lg mx-auto">
                        Thank you for your interest in joining the HAAPE community. 
                        Complete this form to apply for membership in our network.
                    </p>
                </div>

                {/* Progress */}
                <div className="mb-8">
                    <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-medium">Step {step} of 3</span>
                        <span className="text-sm text-muted-foreground">
                            {step === 1 ? 'Organization Info' : step === 2 ? 'Stakeholder Details' : 'Your Info'}
                        </span>
                    </div>
                    <Progress value={progressPercent} className="h-2" />
                </div>

                <form onSubmit={handleSubmit}>
                    {/* Step 1: Organization Info */}
                    {step === 1 && (
                        <Card data-testid="step-1-form">
                            <CardHeader>
                                <CardTitle className="flex items-center gap-2">
                                    <Building2 className="h-5 w-5 text-primary" />
                                    Organization Information
                                </CardTitle>
                                <CardDescription>
                                    Tell us about your organization
                                </CardDescription>
                            </CardHeader>
                            <CardContent className="space-y-6">
                                <div className="space-y-2">
                                    <Label htmlFor="name">Organization Name *</Label>
                                    <Input
                                        id="name"
                                        value={formData.name}
                                        onChange={(e) => handleChange('name', e.target.value)}
                                        placeholder="e.g., Aspiritech"
                                        data-testid="input-name"
                                    />
                                    {errors.name && <p className="text-sm text-destructive">{errors.name}</p>}
                                </div>

                                <div className="space-y-2">
                                    <Label htmlFor="mission">Mission Statement *</Label>
                                    <Textarea
                                        id="mission"
                                        value={formData.mission}
                                        onChange={(e) => handleChange('mission', e.target.value)}
                                        placeholder="What is your organization's mission?"
                                        rows={3}
                                        data-testid="input-mission"
                                    />
                                    {errors.mission && <p className="text-sm text-destructive">{errors.mission}</p>}
                                </div>

                                <div className="space-y-2">
                                    <Label htmlFor="website">Website URL</Label>
                                    <Input
                                        id="website"
                                        type="url"
                                        value={formData.website}
                                        onChange={(e) => handleChange('website', e.target.value)}
                                        placeholder="https://example.org"
                                        data-testid="input-website"
                                    />
                                </div>

                                <div className="grid sm:grid-cols-2 gap-4">
                                    <div className="space-y-2">
                                        <Label htmlFor="state">State *</Label>
                                        <Select value={formData.state} onValueChange={(v) => handleChange('state', v)}>
                                            <SelectTrigger data-testid="select-state">
                                                <SelectValue placeholder="Select state" />
                                            </SelectTrigger>
                                            <SelectContent>
                                                {US_STATES.map((state) => (
                                                    <SelectItem key={state} value={state}>{state}</SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                        {errors.state && <p className="text-sm text-destructive">{errors.state}</p>}
                                    </div>
                                    <div className="space-y-2">
                                        <Label htmlFor="city">City</Label>
                                        <Input
                                            id="city"
                                            value={formData.city}
                                            onChange={(e) => handleChange('city', e.target.value)}
                                            placeholder="e.g., Chicago"
                                            data-testid="input-city"
                                        />
                                    </div>
                                </div>

                                <div className="border-t border-border pt-6">
                                    <h3 className="font-medium mb-4 flex items-center gap-2">
                                        <User className="h-4 w-4 text-primary" />
                                        Organization Contact
                                    </h3>
                                    <div className="grid sm:grid-cols-2 gap-4">
                                        <div className="space-y-2">
                                            <Label htmlFor="contact_name">Contact Name *</Label>
                                            <Input
                                                id="contact_name"
                                                value={formData.contact_name}
                                                onChange={(e) => handleChange('contact_name', e.target.value)}
                                                placeholder="Full name"
                                                data-testid="input-contact-name"
                                            />
                                            {errors.contact_name && <p className="text-sm text-destructive">{errors.contact_name}</p>}
                                        </div>
                                        <div className="space-y-2">
                                            <Label htmlFor="contact_email">Contact Email *</Label>
                                            <Input
                                                id="contact_email"
                                                type="email"
                                                value={formData.contact_email}
                                                onChange={(e) => handleChange('contact_email', e.target.value)}
                                                placeholder="contact@example.org"
                                                data-testid="input-contact-email"
                                            />
                                            {errors.contact_email && <p className="text-sm text-destructive">{errors.contact_email}</p>}
                                        </div>
                                    </div>
                                    <div className="space-y-2 mt-4">
                                        <Label htmlFor="contact_phone">Contact Phone</Label>
                                        <Input
                                            id="contact_phone"
                                            value={formData.contact_phone}
                                            onChange={(e) => handleChange('contact_phone', e.target.value)}
                                            placeholder="(555) 123-4567"
                                            data-testid="input-contact-phone"
                                        />
                                    </div>
                                </div>

                                <div className="flex justify-end pt-4">
                                    <Button type="button" onClick={handleNext} className="gap-2" data-testid="next-step-btn">
                                        Next Step
                                        <ArrowRight className="h-4 w-4" />
                                    </Button>
                                </div>
                            </CardContent>
                        </Card>
                    )}

                    {/* Step 2: Stakeholder Type & Details */}
                    {step === 2 && (
                        <Card data-testid="step-2-form">
                            <CardHeader>
                                <CardTitle className="flex items-center gap-2">
                                    <HeartHandshake className="h-5 w-5 text-primary" />
                                    Stakeholder Details
                                </CardTitle>
                                <CardDescription>
                                    What type of organization is this?
                                </CardDescription>
                            </CardHeader>
                            <CardContent className="space-y-6">
                                <div className="space-y-3">
                                    <Label>Organization Type(s) * <span className="text-sm font-normal text-muted-foreground">— select all that apply</span></Label>
                                    <div className="grid gap-3">
                                        {STAKEHOLDER_TYPES.map((type) => {
                                            const isChecked = formData.stakeholder_types.includes(type.value);
                                            return (
                                                <Label
                                                    key={type.value}
                                                    htmlFor={`type-${type.value}`}
                                                    className={`flex items-start gap-3 p-4 rounded-lg border cursor-pointer transition-colors ${
                                                        isChecked
                                                            ? 'border-primary bg-primary/5' 
                                                            : 'border-border hover:bg-muted/50'
                                                    }`}
                                                >
                                                    <Checkbox
                                                        id={`type-${type.value}`}
                                                        checked={isChecked}
                                                        onCheckedChange={(checked) => {
                                                            const updated = checked
                                                                ? [...formData.stakeholder_types, type.value]
                                                                : formData.stakeholder_types.filter(t => t !== type.value);
                                                            handleChange('stakeholder_types', updated);
                                                        }}
                                                        className="mt-1"
                                                    />
                                                    <div className="flex-1">
                                                        <div className="flex items-center gap-2">
                                                            <type.icon className="h-4 w-4 text-primary" />
                                                            <span className="font-medium">{type.label}</span>
                                                        </div>
                                                        <p className="text-sm text-muted-foreground mt-1">{type.description}</p>
                                                    </div>
                                                </Label>
                                            );
                                        })}
                                    </div>
                                    {errors.stakeholder_types && <p className="text-sm text-destructive">{errors.stakeholder_types}</p>}
                                </div>

                                {/* Conditional fields based on stakeholder type */}
                                {formData.stakeholder_types.includes('Employer') && (
                                    <div className="space-y-4 border-t border-border pt-6">
                                        <div className="space-y-2">
                                            <Label htmlFor="employer_type">Describe type of employer *</Label>
                                            <Textarea
                                                id="employer_type"
                                                value={formData.employer_type}
                                                onChange={(e) => handleChange('employer_type', e.target.value)}
                                                placeholder="e.g., Technology company, Retail, Manufacturing..."
                                                rows={2}
                                            />
                                            {errors.employer_type && <p className="text-sm text-destructive">{errors.employer_type}</p>}
                                        </div>
                                        <div className="space-y-2">
                                            <Label htmlFor="job_types">Types of jobs offered</Label>
                                            <Textarea
                                                id="job_types"
                                                value={formData.job_types}
                                                onChange={(e) => handleChange('job_types', e.target.value)}
                                                placeholder="e.g., Software testing, Data entry, Customer service..."
                                                rows={2}
                                            />
                                        </div>
                                    </div>
                                )}

                                {formData.stakeholder_types.includes('Service/Support Provider') && (
                                    <div className="space-y-4 border-t border-border pt-6">
                                        <div className="space-y-2">
                                            <Label htmlFor="services_for_employment">Services provided for employment *</Label>
                                            <Textarea
                                                id="services_for_employment"
                                                value={formData.services_for_employment}
                                                onChange={(e) => handleChange('services_for_employment', e.target.value)}
                                                placeholder="e.g., Job coaching, resume assistance, interview prep..."
                                                rows={3}
                                            />
                                            {errors.services_for_employment && <p className="text-sm text-destructive">{errors.services_for_employment}</p>}
                                        </div>
                                    </div>
                                )}

                                {formData.stakeholder_types.includes('College or Training Provider') && (
                                    <div className="space-y-4 border-t border-border pt-6">
                                        <div className="flex items-center justify-between">
                                            <Label htmlFor="has_neurodiverse_programs">
                                                Do you currently provide programs for neurodiverse adults?
                                            </Label>
                                            <Switch
                                                id="has_neurodiverse_programs"
                                                checked={formData.has_neurodiverse_programs}
                                                onCheckedChange={(v) => handleChange('has_neurodiverse_programs', v)}
                                            />
                                        </div>
                                        {formData.has_neurodiverse_programs && (
                                            <div className="space-y-2">
                                                <Label htmlFor="program_description">Please describe the programs</Label>
                                                <Textarea
                                                    id="program_description"
                                                    value={formData.program_description}
                                                    onChange={(e) => handleChange('program_description', e.target.value)}
                                                    placeholder="Describe the programs offered..."
                                                    rows={3}
                                                />
                                            </div>
                                        )}
                                    </div>
                                )}

                                {formData.stakeholder_types.includes('High School') && (
                                    <div className="space-y-4 border-t border-border pt-6">
                                        <div className="flex items-center justify-between">
                                            <Label htmlFor="has_ese_transition">
                                                Do you have an ESE transition program for neurodiverse students?
                                            </Label>
                                            <Switch
                                                id="has_ese_transition"
                                                checked={formData.has_ese_transition}
                                                onCheckedChange={(v) => handleChange('has_ese_transition', v)}
                                            />
                                        </div>
                                        {formData.has_ese_transition && (
                                            <div className="space-y-2">
                                                <Label htmlFor="ese_program_description">Please describe the program</Label>
                                                <Textarea
                                                    id="ese_program_description"
                                                    value={formData.ese_program_description}
                                                    onChange={(e) => handleChange('ese_program_description', e.target.value)}
                                                    placeholder="Describe the ESE transition program..."
                                                    rows={3}
                                                />
                                            </div>
                                        )}
                                    </div>
                                )}

                                {formData.stakeholder_types.includes('Transportation') && (
                                    <div className="space-y-4 border-t border-border pt-6">
                                        <div className="flex items-center justify-between">
                                            <Label htmlFor="has_specialized_transport">
                                                Do you offer specialized transportation for disabled adults?
                                            </Label>
                                            <Switch
                                                id="has_specialized_transport"
                                                checked={formData.has_specialized_transport}
                                                onCheckedChange={(v) => handleChange('has_specialized_transport', v)}
                                            />
                                        </div>
                                        {formData.has_specialized_transport && (
                                            <div className="space-y-2">
                                                <Label htmlFor="transport_description">Please describe the services</Label>
                                                <Textarea
                                                    id="transport_description"
                                                    value={formData.transport_description}
                                                    onChange={(e) => handleChange('transport_description', e.target.value)}
                                                    placeholder="Describe the transportation services..."
                                                    rows={3}
                                                />
                                            </div>
                                        )}
                                    </div>
                                )}

                                {formData.stakeholder_types.includes('Government') && (
                                    <div className="space-y-4 border-t border-border pt-6">
                                        <div className="space-y-2">
                                            <Label htmlFor="government_support_description">
                                                How do you support autistic adults? *
                                            </Label>
                                            <Textarea
                                                id="government_support_description"
                                                value={formData.government_support_description}
                                                onChange={(e) => handleChange('government_support_description', e.target.value)}
                                                placeholder="e.g., employment services, funding, vocational rehabilitation..."
                                                rows={3}
                                            />
                                            {errors.government_support_description && <p className="text-sm text-destructive">{errors.government_support_description}</p>}
                                        </div>
                                    </div>
                                )}

                                {/* Other Info (always shown) */}
                                <div className="space-y-4 border-t border-border pt-6">
                                    <div className="space-y-2">
                                        <Label htmlFor="other_info">Other relevant information</Label>
                                        <Textarea
                                            id="other_info"
                                            value={formData.other_info}
                                            onChange={(e) => handleChange('other_info', e.target.value)}
                                            placeholder="Any other information that would be helpful..."
                                            rows={3}
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label htmlFor="nd_population_served">Population served</Label>
                                        <Input
                                            id="nd_population_served"
                                            value={formData.nd_population_served}
                                            onChange={(e) => handleChange('nd_population_served', e.target.value)}
                                            placeholder="e.g., Autistic adults, individuals with ADHD..."
                                        />
                                    </div>
                                </div>

                                <div className="flex justify-between pt-4">
                                    <Button type="button" variant="outline" onClick={handleBack} className="gap-2">
                                        <ArrowLeft className="h-4 w-4" />
                                        Back
                                    </Button>
                                    <Button type="button" onClick={handleNext} className="gap-2">
                                        Next Step
                                        <ArrowRight className="h-4 w-4" />
                                    </Button>
                                </div>
                            </CardContent>
                        </Card>
                    )}

                    {/* Step 3: Requester Info */}
                    {step === 3 && (
                        <Card data-testid="step-3-form">
                            <CardHeader>
                                <CardTitle className="flex items-center gap-2">
                                    <User className="h-5 w-5 text-primary" />
                                    Your Information
                                </CardTitle>
                                <CardDescription>
                                    Tell us about yourself so we can follow up if needed
                                </CardDescription>
                            </CardHeader>
                            <CardContent className="space-y-6">
                                <Alert>
                                    <Info className="h-4 w-4" />
                                    <AlertDescription>
                                        Your information will only be used to verify this application 
                                        and contact you if we have questions.
                                    </AlertDescription>
                                </Alert>

                                <div className="space-y-2">
                                    <Label htmlFor="requester_name">Your Name *</Label>
                                    <Input
                                        id="requester_name"
                                        value={formData.requester_name}
                                        onChange={(e) => handleChange('requester_name', e.target.value)}
                                        placeholder="Your full name"
                                        data-testid="input-requester-name"
                                    />
                                    {errors.requester_name && <p className="text-sm text-destructive">{errors.requester_name}</p>}
                                </div>

                                <div className="space-y-2">
                                    <Label htmlFor="requester_email">Your Email *</Label>
                                    <Input
                                        id="requester_email"
                                        type="email"
                                        value={formData.requester_email}
                                        onChange={(e) => handleChange('requester_email', e.target.value)}
                                        placeholder="your@email.com"
                                        data-testid="input-requester-email"
                                    />
                                    {errors.requester_email && <p className="text-sm text-destructive">{errors.requester_email}</p>}
                                </div>

                                <div className="space-y-2">
                                    <Label htmlFor="requester_role">Your Role *</Label>
                                    <Select 
                                        value={formData.requester_role} 
                                        onValueChange={(v) => handleChange('requester_role', v)}
                                    >
                                        <SelectTrigger data-testid="select-role">
                                            <SelectValue placeholder="Select your role" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {REQUESTER_ROLES.map((role) => (
                                                <SelectItem key={role} value={role}>{role}</SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                    {errors.requester_role && <p className="text-sm text-destructive">{errors.requester_role}</p>}
                                </div>

                                {formData.requester_role === 'Chapter Director' && (
                                    <div className="space-y-2">
                                        <Label htmlFor="chapter_location">Chapter Location</Label>
                                        <Input
                                            id="chapter_location"
                                            value={formData.chapter_location}
                                            onChange={(e) => handleChange('chapter_location', e.target.value)}
                                            placeholder="e.g., South Florida Chapter"
                                        />
                                    </div>
                                )}

                                {/* Terms & Conditions Agreement */}
                                <div className="space-y-3 border-t border-border pt-6">
                                    <div className="flex items-start space-x-3">
                                        <Checkbox
                                            id="agreed_to_terms"
                                            checked={formData.agreed_to_terms}
                                            onCheckedChange={(checked) => handleChange('agreed_to_terms', checked)}
                                            data-testid="terms-checkbox"
                                        />
                                        <div className="grid gap-1.5 leading-none">
                                            <label
                                                htmlFor="agreed_to_terms"
                                                className="text-sm font-medium leading-relaxed cursor-pointer"
                                            >
                                                I agree to the{' '}
                                                <Link 
                                                    to="/terms" 
                                                    target="_blank"
                                                    className="text-primary hover:underline inline-flex items-center gap-1"
                                                    onClick={(e) => e.stopPropagation()}
                                                >
                                                    Terms & Conditions
                                                    <ExternalLink className="h-3 w-3" />
                                                </Link>
                                                {' '}on behalf of the organization being submitted *
                                            </label>
                                            <p className="text-xs text-muted-foreground">
                                                By checking this box, you acknowledge that you have read and agree to HAAPE's Terms & Conditions.
                                            </p>
                                        </div>
                                    </div>
                                    {errors.agreed_to_terms && (
                                        <p className="text-sm text-destructive ml-7">{errors.agreed_to_terms}</p>
                                    )}
                                </div>

                                <div className="flex justify-between pt-4">
                                    <Button type="button" variant="outline" onClick={handleBack} className="gap-2">
                                        <ArrowLeft className="h-4 w-4" />
                                        Back
                                    </Button>
                                    <Button type="submit" disabled={submitting} className="gap-2" data-testid="submit-btn">
                                        {submitting ? (
                                            <>Submitting...</>
                                        ) : (
                                            <>
                                                Submit Application
                                                <Send className="h-4 w-4" />
                                            </>
                                        )}
                                    </Button>
                                </div>
                            </CardContent>
                        </Card>
                    )}
                </form>
            </div>
        </div>
    );
};

export default ApplyMembershipPage;
